/**
 * paste.ts — the client half of the paste-to-syntax contract (Track C, C-WP5).
 *
 * Two things live here:
 *   1. `PASTE_MAX_CHARS` — the client-side character cap on the paste textarea.
 *   2. `PasteConvertRequest` / `PasteConvertResponse` — the wire shapes for the
 *      `assistant.pasteConvert` procedure the server sibling (w4-paste-route)
 *      adds to the oRPC router.
 *
 * ── Why a mirrored constant, not an import ──────────────────────────────────
 * The server enforces the SAME character cap in its RPC schema so the two limits
 * cannot diverge (a paste the client accepts must never be rejected server-side,
 * and vice versa). The server derives its cap from `apps/api/src/assistant/
 * models.ts`:
 *
 *   PASTE_MAX_CHARS = (T1_INPUT_TOKEN_BUDGET − PASTE_PROMPT_RESERVATION_TOKENS)
 *                     × CHARS_PER_TOKEN
 *
 * with the plan-time (provisional) values:
 *
 *   T1_INPUT_TOKEN_BUDGET             = 8_000   (models.ts)
 *   PASTE_PROMPT_RESERVATION_TOKENS   = 5_000   (headroom for the grammar-spec
 *                                                 system prompt + JSON schema)
 *   CHARS_PER_TOKEN                   = 4       (models.ts)
 *   ⇒ (8_000 − 5_000) × 4            = 12_000
 *
 * This package (`@proof/client-core`) CANNOT import `apps/api` — that package
 * already depends on `@proof/api` for the router type, so importing the model
 * constants back would create a workspace dependency cycle. Instead the literal
 * is mirrored here and a drift-guard vitest in `apps/api` asserts the server
 * constant equals this value (the DEFAULT_CHARS_PER_TOKEN drift-test pattern).
 * `paste.test.ts` beside this file pins the client half to the derivation so a
 * change on either side surfaces as a failing test rather than a silent skew.
 *
 * All three numbers above are PROVISIONAL (flagged until the R3/R4 pricing
 * signature, same one-file discipline as models.ts). Re-derive at each pricing
 * signature.
 *
 * Build plan ref: .plan/implementation/2026-07-10-track-c-ai-mcp-build-plan.md
 *   C-WP5 — Paste-to-syntax.
 */

/**
 * The T1 hard input-token budget (mirrors `apps/api` models.ts
 * `T1_INPUT_TOKEN_BUDGET`). Provisional.
 */
export const PASTE_T1_INPUT_TOKEN_BUDGET = 8_000;

/**
 * Tokens reserved for the grammar-spec system prompt + JSON schema, subtracted
 * from the input budget before the user's paste is allowed to fill the rest.
 * Provisional; the server sibling owns the authoritative value and the drift
 * guard reconciles the two.
 */
export const PASTE_PROMPT_RESERVATION_TOKENS = 5_000;

/** Characters-per-token estimate (mirrors models.ts `CHARS_PER_TOKEN`). */
export const PASTE_CHARS_PER_TOKEN = 4;

/**
 * The client-side character cap on the paste textarea. Derived from the T1 input
 * budget so the client cap and the server RPC-schema cap cannot drift. See the
 * module header for the full derivation.
 */
export const PASTE_MAX_CHARS =
  (PASTE_T1_INPUT_TOKEN_BUDGET - PASTE_PROMPT_RESERVATION_TOKENS) * PASTE_CHARS_PER_TOKEN;

/** Request shape for `assistant.pasteConvert`. */
export interface PasteConvertRequest {
  /** The recipe title the user typed (shared with the blank-create title). */
  title: string;
  /** The raw pasted recipe text. Bounded client-side by {@link PASTE_MAX_CHARS}. */
  pastedText: string;
}

/**
 * Response shape for `assistant.pasteConvert`. On success the server has created
 * the recipe row (draft-only, no version record yet) and seeded the converted
 * body into its Draft — the client navigates to the editor with the Draft open.
 */
export interface PasteConvertResponse {
  /** UUID of the newly-created recipe. */
  recipeUuid: string;
}

/**
 * The typed error codes `assistant.pasteConvert` can surface.
 *   - `assistant_not_enabled` — the user has not opted in (Settings → Assistant).
 *   - `assistant_unavailable` — the global kill switch is off (env flag unset);
 *      the assistant is dark for everyone right now.
 *   - `AI_OPS_EXCEEDED` — the per-user op cap is spent (shared code; mapped to
 *      its reset-aware copy in `@proof/client-core/errors`).
 *   - `paste_too_large` — the pasted text exceeds the server cap.
 *   - `paste_convert_failed` — conversion still invalid after the repair loop;
 *      NO recipe was created (the text is unchanged).
 *   - `paste_convert_seed_failed` — the recipe row WAS created but seeding the
 *      converted body into its Draft failed; the error data carries `recipeUuid`
 *      so the client can open the created recipe rather than mint a duplicate.
 */
export type PasteConvertErrorCode =
  | "assistant_not_enabled"
  | "assistant_unavailable"
  | "AI_OPS_EXCEEDED"
  | "paste_too_large"
  | "paste_convert_failed"
  | "paste_convert_seed_failed";
